import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { CourseCatalogRow } from '@/components/courses/CourseCatalogRow';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';

interface CourseRow {
  id: string;
  name: string;
  code?: string;
  shortDescription?: string;
  status?: string;
  deletedAt?: string | null;
  categoryId?: string;
  category?: { id: string; name: string };
  primaryInstructor?: { id: string; name: string };
}

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

type StatusTab = '' | 'draft' | 'published' | 'archived';
type SortPreset = 'recent' | 'code' | 'title';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
};

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicados' },
  { value: 'archived', label: 'Archivados' },
];

const PER_PAGE = 20;

function flattenCategories(nodes: CategoryNode[], depth = 0): { id: string; indentedName: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, indentedName: `${'  '.repeat(depth)}${node.name}` },
    ...(node.children ? flattenCategories(node.children, depth + 1) : []),
  ]);
}

function sortPresetToApplied(preset: SortPreset): { sort: string; direction: string } {
  switch (preset) {
    case 'code':
      return { sort: 'code', direction: 'asc' };
    case 'title':
      return { sort: 'name', direction: 'asc' };
    default:
      return { sort: 'created_at', direction: 'desc' };
  }
}

function sortPresetFromApplied(sort: string, direction: string): SortPreset {
  if (sort === 'code' && direction === 'asc') return 'code';
  if (sort === 'name' && direction === 'asc') return 'title';
  return 'recent';
}

function parseStatus(value: string | null): StatusTab {
  if (value === 'draft' || value === 'published' || value === 'archived') return value;
  return '';
}

function parsePage(value: string | null): number {
  const n = Number.parseInt(value ?? '1', 10);
  return Number.isFinite(n) && n > 1 ? n : 1;
}

function catalogListParams(next: {
  search: string;
  status: StatusTab;
  category_id: string;
  instructor_id: string;
  sort: string;
  direction: string;
  page: number;
}): URLSearchParams {
  const params = new URLSearchParams();
  const search = next.search.trim();
  if (search) params.set('search', search);
  if (next.status) params.set('status', next.status);
  if (next.category_id) params.set('category_id', next.category_id);
  if (next.instructor_id) params.set('instructor_id', next.instructor_id);
  if (next.sort !== 'created_at' || next.direction !== 'desc') {
    params.set('sort', next.sort);
    params.set('direction', next.direction);
  }
  if (next.page > 1) params.set('page', String(next.page));
  return params;
}

export function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const canCreateCourse = hasPermission(PERMISSIONS.CURSOS_CREAR);
  const applied = useMemo(
    () => ({
      search: searchParams.get('search') ?? '',
      status: parseStatus(searchParams.get('status')),
      category_id: searchParams.get('category_id') ?? '',
      instructor_id: searchParams.get('instructor_id') ?? '',
      sort: searchParams.get('sort') ?? 'created_at',
      direction: searchParams.get('direction') ?? 'desc',
    }),
    [searchParams],
  );
  const [search, setSearch] = useState(applied.search);
  const [statusTab, setStatusTab] = useState<StatusTab>(applied.status);
  const [categoryId, setCategoryId] = useState(applied.category_id);
  const [instructorId, setInstructorId] = useState(applied.instructor_id);
  const [sortPreset, setSortPreset] = useState<SortPreset>(
    sortPresetFromApplied(applied.sort, applied.direction),
  );
  const [showMoreFilters, setShowMoreFilters] = useState(
    Boolean(applied.category_id || applied.instructor_id || applied.sort !== 'created_at'),
  );

  useEffect(() => {
    setSearch(applied.search);
    setStatusTab(applied.status);
    setCategoryId(applied.category_id);
    setInstructorId(applied.instructor_id);
    setSortPreset(sortPresetFromApplied(applied.sort, applied.direction));
    if (applied.category_id || applied.instructor_id || applied.sort !== 'created_at') {
      setShowMoreFilters(true);
    }
  }, [applied]);

  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading, error } = useQuery({
    queryKey: ['courses', debouncedSearch.trim()],
    queryFn: () =>
      listResource<CourseRow>('/courses', {
        perPage: 200,
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      }),
  });

  const { data: categoriesTree } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryNode[]>('/categories'),
  });

  const rows = data?.data ?? [];
  const categories = flattenCategories(categoriesTree ?? []);

  const instructors = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      if (row.primaryInstructor) map.set(row.primaryInstructor.id, row.primaryInstructor.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const statusCounts = useMemo(() => {
    const active = rows.filter((row) => !row.deletedAt);
    return {
      all: active.length,
      draft: active.filter((row) => (row.status ?? 'draft') === 'draft').length,
      published: active.filter((row) => row.status === 'published').length,
      archived: active.filter((row) => row.status === 'archived').length,
    };
  }, [rows]);

  const sortApplied = sortPresetToApplied(sortPreset);
  const filtered = useMemo(() => {
    let list = rows.filter((row) => {
      if (row.deletedAt) return false;
      const haystack = [row.name, row.code, row.shortDescription, row.category?.name].filter(Boolean).join(' ');
      if (!matchesSearch(haystack, search)) return false;
      if (statusTab && row.status !== statusTab) return false;
      if (categoryId && row.categoryId !== categoryId && row.category?.id !== categoryId) return false;
      if (instructorId && row.primaryInstructor?.id !== instructorId) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      const dir = sortApplied.direction === 'asc' ? 1 : -1;
      switch (sortApplied.sort) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'code':
          return (a.code ?? '').localeCompare(b.code ?? '') * dir;
        default:
          return 0;
      }
    });

    return list;
  }, [rows, search, statusTab, categoryId, instructorId, sortApplied.sort, sortApplied.direction]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const requestedPage = parsePage(searchParams.get('page'));
  const page = Math.min(requestedPage, totalPages);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const rangeEnd = Math.min(page * PER_PAGE, filtered.length);

  function writeFilters(next: {
    search: string;
    status: StatusTab;
    category_id: string;
    instructor_id: string;
    sort: string;
    direction: string;
    page: number;
  }) {
    setSearchParams(catalogListParams(next), { replace: true });
  }

  function applyFilters(next: {
    search?: string;
    status?: StatusTab;
    category_id?: string;
    instructor_id?: string;
    sortPreset?: SortPreset;
  } = {}) {
    const nextSort = sortPresetToApplied(next.sortPreset ?? sortPreset);
    writeFilters({
      search: next.search ?? search,
      status: next.status ?? statusTab,
      category_id: next.category_id ?? categoryId,
      instructor_id: next.instructor_id ?? instructorId,
      sort: nextSort.sort,
      direction: nextSort.direction,
      page: 1,
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
  }

  function handleStatusTab(tab: StatusTab) {
    setStatusTab(tab);
    applyFilters({ status: tab });
  }

  function goToPage(nextPage: number) {
    writeFilters({
      ...applied,
      page: nextPage,
    });
  }

  function resetFilters() {
    setSearch('');
    setStatusTab('');
    setCategoryId('');
    setInstructorId('');
    setSortPreset('recent');
    setShowMoreFilters(false);
    setSearchParams({}, { replace: true });
  }

  return (
    <div className="lms-course-catalog lms-course-catalog--admin lms-site-admin-hub-page font-sans">
      <header className="lms-course-catalog__page-header">
        <div className="flex items-start gap-4 min-w-0">
          <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Cursos</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Gestión del catálogo académico de cursos
            </p>
          </div>
        </div>
        {canCreateCourse && (
          <Link to="/courses/create" className="lms-course-catalog__new-btn">
            <span className="material-symbols-outlined" aria-hidden="true">
              add
            </span>
            Nuevo curso
          </Link>
        )}
      </header>

      <form method="get" action="/courses" className="lms-course-catalog__panel" onSubmit={handleSubmit}>
        <div className="lms-course-catalog__tabs-row">
          <div className="lms-course-catalog__tabs" role="group" aria-label="Filtrar por estado">
            {STATUS_TABS.map((tab) => {
              const countKey =
                tab.value === '' ? 'all' : tab.value === 'published' ? 'published' : tab.value;
              const count = statusCounts[countKey as keyof typeof statusCounts];
              return (
                <button
                  key={tab.value || 'all'}
                  type="button"
                  className={`lms-course-catalog__tab${statusTab === tab.value ? ' lms-course-catalog__tab--active' : ''}`}
                  onClick={() => handleStatusTab(tab.value)}
                >
                  {tab.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="lms-course-catalog__search-row">
          <SearchInput
            name="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              applyFilters({ search: value });
            }}
            placeholder="Buscar cursos por nombre, código o categoría..."
            aria-label="Buscar cursos"
            className="lms-course-catalog__search"
          />
          <div className="lms-course-catalog__search-actions">
            <button
              type="button"
              className="lms-course-catalog__filters-toggle lms-course-catalog__filters-toggle--panel"
              onClick={() => setShowMoreFilters((value) => !value)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                filter_list
              </span>
              <span>{showMoreFilters ? 'Ocultar filtros' : 'Mostrar más filtros'}</span>
              <span className="material-symbols-outlined" aria-hidden="true">
                {showMoreFilters ? 'expand_less' : 'expand_more'}
              </span>
            </button>
          </div>
        </div>

        {showMoreFilters ? (
          <div className="lms-course-catalog__advanced">
            <label className="lms-course-catalog__advanced-field">
              <span>Categoría</span>
              <select
                name="category_id"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  applyFilters({ category_id: e.target.value });
                }}
              >
                <option value="">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.indentedName}
                  </option>
                ))}
              </select>
            </label>
            <label className="lms-course-catalog__advanced-field">
              <span>Instructor a cargo</span>
              <select
                name="instructor_id"
                value={instructorId}
                onChange={(e) => {
                  setInstructorId(e.target.value);
                  applyFilters({ instructor_id: e.target.value });
                }}
              >
                <option value="">Todos los docentes</option>
                {instructors.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="lms-course-catalog__advanced-field">
              <span>Ordenación</span>
              <select
                name="sort_preset"
                value={sortPreset}
                onChange={(e) => {
                  const next = e.target.value as SortPreset;
                  setSortPreset(next);
                  applyFilters({ sortPreset: next });
                }}
              >
                <option value="recent">Recientes primero</option>
                <option value="code">Código de curso</option>
                <option value="title">Alfabético (A-Z)</option>
              </select>
            </label>
          </div>
        ) : null}
      </form>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : filtered.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    school
                  </span>
                </div>
                <h3>No hay cursos</h3>
                <p>No se encontraron cursos con los filtros aplicados.</p>
                <button type="button" className="lms-gradebook__clear" onClick={resetFilters}>
                  Restablecer filtros
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Curso
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Categoría
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Responsable
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((course) => (
                      <CourseCatalogRow
                        key={course.id}
                        id={course.id}
                        name={course.name}
                        code={course.code}
                        shortDescription={course.shortDescription}
                        status={course.status ?? 'draft'}
                        statusLabel={STATUS_LABELS[course.status ?? ''] ?? course.status ?? 'Borrador'}
                        categoryName={course.category?.name}
                        instructorName={course.primaryInstructor?.name}
                        trashed={Boolean(course.deletedAt)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de{' '}
                  <strong>{filtered.length}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={page <= 1}
                    onClick={() => goToPage(Math.max(1, page - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{page}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={page >= totalPages}
                    onClick={() => goToPage(Math.min(totalPages, page + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { CourseBuilderPage } from './CourseBuilderPage';
